import axiosInstance from 'services/api/config/api';

export interface BuyPhoneNumbersRequest {
    tenantId: string;
    areaCode: number;
    count: number;
    workflowId?: string;
}

export interface BuyPhoneNumbersResponse {
    message: string;
    purchased_numbers?: string[];
    phoneNumbers?: string[];
    count: number;
}

export const buyPhoneNumbers = (data: BuyPhoneNumbersRequest): Promise<BuyPhoneNumbersResponse> => {
    return axiosInstance
        .post<BuyPhoneNumbersResponse>('/telephony/buy-phone-numbers', data)
        .then((res: { data: BuyPhoneNumbersResponse }) => res.data);
};

export interface SyncTrustProductsRequest {
    tenantId: string;
}

export interface SyncTrustProductsResponse {
    message: string;
    businessProfileSid?: string;
    cnamTrustProductSid?: string;
    shakenStirTrustProductSid?: string;
    updated: boolean;
}

export const syncTrustProducts = (data: SyncTrustProductsRequest): Promise<SyncTrustProductsResponse> => {
    return axiosInstance
        .post<SyncTrustProductsResponse>('/telephony/sync-trust-products', data)
        .then((res: { data: SyncTrustProductsResponse }) => res.data);
};

export interface SyncAllTenantsLivekitResult {
    totalTenants: number;
    processedTenants: number;
    skippedTenants: number;
    failedTenants: number;
    totalTrunks: number;
    syncedTrunks: number;
    failedTrunks: number;
    warnings?: string[];
    message: string;
}

export const syncLivekitTrunks = (): Promise<SyncAllTenantsLivekitResult> => {
    return axiosInstance
        .post<SyncAllTenantsLivekitResult>('/telephony/sync-livekit-trunks')
        .then((res: { data: SyncAllTenantsLivekitResult }) => res.data);
};

export interface SpamCheckResult {
    phoneNumber: string;
    displayResult: string;
}

export interface ReplaceSpamNumbersRequest {
    spamCheckResults: SpamCheckResult[];
}

export interface SpamReplacementResult {
    oldNumber: string;
    newNumber: string;
    tenantId: string;
    tenantName: string;
    areaCode: number;
    releaseError?: string;
    purchaseError?: string;
    success: boolean;
}

export interface ReplaceSpamNumbersResponse {
    totalSpamNumbers: number;
    replaced: number;
    failed: number;
    notFound: number;
    results: SpamReplacementResult[];
    message: string;
}

export const replaceSpamNumbers = (data: ReplaceSpamNumbersRequest): Promise<ReplaceSpamNumbersResponse> => {
    return axiosInstance
        .post<ReplaceSpamNumbersResponse>('/telephony/replace-spam-numbers', data)
        .then((res: { data: ReplaceSpamNumbersResponse }) => res.data);
};

export interface DeleteTwilioAccountRequest {
    tenantId: string;
}

export interface DeleteTwilioAccountResponse {
    message: string;
    deletedNumbers: number;
    accountClosed: boolean;
}

export const deleteTwilioAccount = (data: DeleteTwilioAccountRequest): Promise<DeleteTwilioAccountResponse> => {
    return axiosInstance
        .put<DeleteTwilioAccountResponse>('/telephony/delete-account', data)
        .then((res: { data: DeleteTwilioAccountResponse }) => res.data);
};

export interface ReassignTrunkNumbersRequest {
    tenantId: string;
    oldTrunkSid: string;
}

export interface ReassignTrunkNumbersResponse {
    message: string;
}

export const reassignTrunkNumbers = (data: ReassignTrunkNumbersRequest): Promise<ReassignTrunkNumbersResponse> => {
    return axiosInstance
        .put<ReassignTrunkNumbersResponse>('/telephony/reassign-trunk-numbers', data)
        .then((res: { data: ReassignTrunkNumbersResponse }) => res.data);
};

export interface CreateWorkflowTrunkRequest {
    tenantId: string;
    workflowId: string;
}

export interface CreateWorkflowTrunkResponse {
    twilioTrunkSid: string;
    livekitTrunkSid: string;
    terminationURI: string;
    message: string;
}

export const createWorkflowTrunk = (data: CreateWorkflowTrunkRequest): Promise<CreateWorkflowTrunkResponse> => {
    return axiosInstance
        .post<CreateWorkflowTrunkResponse>('/telephony/create-workflow-trunk', data)
        .then((res: { data: CreateWorkflowTrunkResponse }) => res.data);
};

export interface TestSpamCarrierRequest {
    tenantId?: string;
}

export const testSpamCarrier = (data: TestSpamCarrierRequest): void => {
    const params = data.tenantId ? { tenant_id: data.tenantId } : {};
    // Fire and forget - don't wait for response as it will timeout
    axiosInstance.post('/telephony/carrier-tests/production', {}, { params }).catch((error: unknown) => {
        console.error('Error initiating spam carrier test:', error);
    });
};

export interface ReduceWorkflowNumbersRequest {
    tenantId: string;
    workflowId: string;
    keepNumbers: number;
    includeOrphans?: boolean;
}

export interface ReduceWorkflowNumbersResponse {
    scope: 'workflow' | 'base';
    twilioTrunkSid: string;
    livekitTrunkSid: string;
    before: number;
    after: number;
    releasedNumbers: string[];
    softDeletedRows: number;
    livekitOutboundSynced: boolean;
    livekitInboundSynced?: boolean;
    warnings: string;
    message: string;
}

export const reduceWorkflowNumbers = (data: ReduceWorkflowNumbersRequest): Promise<ReduceWorkflowNumbersResponse> => {
    return axiosInstance
        .post<ReduceWorkflowNumbersResponse>('/telephony/reduce-workflow-numbers', data)
        .then((res: { data: ReduceWorkflowNumbersResponse }) => res.data);
};

export interface WorkflowWithOwnSip {
    workflowId: string;
    workflowTitle: string;
}

export interface WorkflowsWithOwnSipResponse {
    workflows: WorkflowWithOwnSip[];
    count: number;
}

export const getWorkflowsWithOwnSip = (tenantId: string): Promise<WorkflowsWithOwnSipResponse> => {
    return axiosInstance
        .get<WorkflowsWithOwnSipResponse>('/telephony/workflows-with-own-sip', {
            params: { tenantId },
        })
        .then((res: { data: WorkflowsWithOwnSipResponse }) => res.data);
};

const phoneNumberService = {
    buyPhoneNumbers,
    syncTrustProducts,
    syncLivekitTrunks,
    replaceSpamNumbers,
    deleteTwilioAccount,
    reassignTrunkNumbers,
    createWorkflowTrunk,
    testSpamCarrier,
    reduceWorkflowNumbers,
    getWorkflowsWithOwnSip,
};

export default phoneNumberService;
